/**
 * Invoice editor overlay opened without leaving the current dashboard tab.
 *
 * Used by the conversation invoice rail chip (click → open the editor),
 * the global Payments page (edit pencil), and the post-create flow
 * (deep-link into the editor after Send). Workbench invoice artifacts
 * embed InvoiceEditor inline instead.
 *
 * `mode` controls layout:
 *   - `fullscreen` — teleported over the entire viewport
 *   - `inset` — fills the main content pane beside the dashboard nav
 *
 * `composerOpen` toggles the native cover-email composer once Send is clicked
 * from inside the editor's action bar.
 */
import { ref, onBeforeUnmount } from 'vue'
import type { Invoice } from '@/api/invoices.api'

export type InvoiceEditorOverlayLayout = 'fullscreen' | 'inset'

const overlayInvoiceId = ref<string | null>(null)
const overlayLayout = ref<InvoiceEditorOverlayLayout>('fullscreen')
const composerInvoiceId = ref<string | null>(null)
const composerIsRevision = ref(false)
const composerJustCreated = ref(false)

type SentListener = (invoice: Invoice) => void
const sentListeners = new Set<SentListener>()

type SavedListener = (invoice: Invoice) => void
const savedListeners = new Set<SavedListener>()

export function notifyCoverEmailSent(invoice: Invoice) {
    for (const cb of sentListeners) {
        try { cb(invoice) } catch { /* ignore listener errors */ }
    }
}

export function notifyInvoiceSaved(invoice: Invoice) {
    for (const cb of savedListeners) {
        try { cb(invoice) } catch { /* ignore listener errors */ }
    }
}

export function useInvoiceEditorOverlay() {
    function openInvoiceEditor(
        invoiceId: string,
        layout: InvoiceEditorOverlayLayout = 'fullscreen',
    ) {
        if (!invoiceId) return
        overlayInvoiceId.value = invoiceId
        overlayLayout.value = layout
    }

    function closeInvoiceEditor() {
        overlayInvoiceId.value = null
        overlayLayout.value = 'fullscreen'
    }

    function openCoverEmailComposer(
        invoiceId: string,
        opts: { isRevision?: boolean; justCreated?: boolean } = {},
    ) {
        if (!invoiceId) return
        composerInvoiceId.value = invoiceId
        composerIsRevision.value = !!opts.isRevision
        composerJustCreated.value = !!opts.justCreated
    }

    function closeCoverEmailComposer() {
        composerInvoiceId.value = null
        composerIsRevision.value = false
        composerJustCreated.value = false
    }

    /**
     * Subscribe to "cover email sent" events. Used by tables/pages that
     * need to refetch their list once an invoice is sent from the
     * (globally-mounted) composer. Returns an unsubscribe function and is
     * automatically torn down on component unmount.
     */
    function onCoverEmailSent(cb: SentListener): () => void {
        sentListeners.add(cb)
        const off = () => { sentListeners.delete(cb) }
        try { onBeforeUnmount(off) } catch { /* outside setup */ }
        return off
    }

    /**
     * Subscribe to "invoice saved" events from the globally-mounted editor.
     * Used by Payments / deliverable tabs to refresh their list without a
     * full page reload. Auto-unsubscribes on unmount.
     */
    function onInvoiceSaved(cb: SavedListener): () => void {
        savedListeners.add(cb)
        const off = () => { savedListeners.delete(cb) }
        try { onBeforeUnmount(off) } catch { /* outside setup */ }
        return off
    }

    return {
        overlayInvoiceId,
        overlayLayout,
        openInvoiceEditor,
        closeInvoiceEditor,

        composerInvoiceId,
        composerIsRevision,
        composerJustCreated,
        openCoverEmailComposer,
        closeCoverEmailComposer,

        onCoverEmailSent,
        onInvoiceSaved,
    }
}
